import { EMAIL_DOMAIN_LIST } from "src/constants/constants";
import { getContrastYIQ } from "src/helpers/whiteLabel";
import apolloClient from "src/state/apolloClient";
import {
  GetBrandInfoQuery,
  GetBrandInfoQueryVariables,
  GetBrandInfoDocument,
  GetBrandInfoPayload,
} from "src/types.g";

export type BrandInfo = {
  name: string;
  domain: string;
  claimed: boolean;
  description: string;
  links: { name: string; url: string }[];
  logos: {
    type: string;
    theme: string;
    formats: {
      src: string;
      background: string;
      format: string;
      size: number;
      height: number;
      width: number;
    }[];
  }[];
  colors: { hex: string; type: string; brightness: number }[];
  fonts: { name: string; type: string; origin: string; originId: string }[];
  images: {
    type: string;
    formats: {
      src: string;
      background: string;
      format: string;
      height: number;
      width: number;
      size: number;
    }[];
  }[];
};

export const toBrandInfo = (payload: GetBrandInfoPayload) => {
  if (payload === null) {
    return undefined;
  }
  return {
    name: payload!.name!,
    domain: payload?.name!,
    claimed: payload?.claimed!,
    description: payload?.description!,
    links: payload!.links!.map((data) => ({
      name: data?.name as string,
      url: data?.url as string,
    })),
    logos: payload!.logos!.map((logo) => ({
      type: logo!.type as string,
      theme: logo!.theme as string,
      formats: logo!.formats!.map((fmt) => ({
        src: fmt?.src as string,
        background: fmt?.background as string,
        format: fmt?.format as string,
        size: fmt?.size as number,
        height: fmt?.height as number,
        width: fmt?.width as number,
      })),
    })),
    colors: payload!.colors!.map((color) => ({
      hex: color?.hex as string,
      type: color?.type as string,
      brightness: color?.brightness as number,
    })),
    fonts: payload!.fonts!.map((font) => ({
      name: font?.name as string,
      type: font?.type as string,
      origin: font?.origin as string,
      originId: font?.originId as string,
    })),
    images: payload!.images!.map((image) => ({
      type: image!.type as string,
      formats: image!.formats!.map((fmt) => ({
        src: fmt?.src as string,
        background: fmt?.background as string,
        format: fmt?.format as string,
        size: fmt?.size as number,
        height: fmt?.height as number,
        width: fmt?.width as number,
      })),
    })),
  };
};

export const queryBrandInfo = async (
  domainName: string
): Promise<BrandInfo | undefined> => {
  if (EMAIL_DOMAIN_LIST.includes(domainName)) {
    return undefined;
  }

  try {
    const { data } = await apolloClient.query<
      GetBrandInfoQuery,
      GetBrandInfoQueryVariables
    >({
      query: GetBrandInfoDocument,
      variables: {
        domainName,
      },
    });
    return toBrandInfo(data?.getBrandInfo);
  } catch {
    return undefined;
  }
};

export const getLogoSrc = (
  brandInfo: BrandInfo | undefined
): string | undefined => {
  if (brandInfo && brandInfo.logos.length) {
    const logo =
      brandInfo.logos.find((logo) => logo.type === "icon") ||
      brandInfo.logos.find((logo) => logo.type === "symbol") ||
      brandInfo.logos.find((logo) => logo.type === "logo");
    return logo?.formats.find(
      (l) =>
        (l.format === "png" || l.format === "jpeg" || l.format === "jpg") &&
        l.width &&
        l.height &&
        l.width < 600 &&
        l.height < 600
    )?.src;
  } else {
    return undefined;
  }
};

export const getColors = (brandInfo: BrandInfo | undefined) => {
  const defaultBackgroundColor = "#1050C2";

  if (brandInfo && brandInfo.colors.length) {
    const backgroundColor =
      brandInfo.colors.find((color) => color.type === "accent")?.hex ||
      brandInfo.colors.find((color) => color.type === "brand")?.hex ||
      brandInfo.colors.find((color) => color.type === "dark")?.hex ||
      defaultBackgroundColor;

    return {
      textColor: getContrastYIQ(backgroundColor),
      backgroundColor,
    };
  } else {
    return {
      textColor: "#ffffff",
      backgroundColor: defaultBackgroundColor,
    };
  }
};
